/**
 * SRT Generation Utilities
 * 
 * Functions for generating and formatting SubRip Subtitle (SRT) content.
 */

import { Clip } from '../types';

type SubtitleTimingMode = 'timeline' | 'source' | 'relative';

interface NormalizableSubtitle {
    start: number;
    end: number;
    text: string;
    hidden?: boolean;
}

function inWindow(value: number, min: number, max: number, tolerance = 0.25): boolean {
    return value >= (min - tolerance) && value <= (max + tolerance);
}

function detectSubtitleTimingMode(clip: Clip, subtitles: NormalizableSubtitle[]): SubtitleTimingMode {
    if (subtitles.length === 0) return 'timeline';

    const clipStart = clip.start;
    const clipEnd = clip.start + clip.duration;
    const trimStart = clip.trimStart || 0;
    const trimEnd = trimStart + clip.duration;

    let timelineVotes = 0;
    let sourceVotes = 0;
    let relativeVotes = 0;

    for (const sub of subtitles) {
        if (!Number.isFinite(sub.start)) continue;
        if (inWindow(sub.start, clipStart, clipEnd)) timelineVotes++;
        if (inWindow(sub.start, trimStart, trimEnd)) sourceVotes++;
        if (inWindow(sub.start, 0, clip.duration)) relativeVotes++;
    }

    if (timelineVotes >= sourceVotes && timelineVotes >= relativeVotes) return 'timeline';
    if (sourceVotes >= relativeVotes) return 'source';
    return 'relative';
}

function normalizeSubtitleToTimeline(
    clip: Clip,
    sub: NormalizableSubtitle,
    mode: SubtitleTimingMode
): { start: number; end: number; text: string } | null {
    const trimStart = clip.trimStart || 0;
    const clipStart = clip.start;
    const clipEnd = clip.start + clip.duration;

    let mappedStart = sub.start;
    let mappedEnd = sub.end;

    if (mode === 'source') {
        mappedStart = clipStart + (sub.start - trimStart);
        mappedEnd = clipStart + (sub.end - trimStart);
    } else if (mode === 'relative') {
        mappedStart = clipStart + sub.start;
        mappedEnd = clipStart + sub.end;
    }

    if (!Number.isFinite(mappedStart) || !Number.isFinite(mappedEnd)) return null;

    const clampedStart = Math.max(clipStart, mappedStart);
    const clampedEnd = Math.min(clipEnd, mappedEnd);
    if (clampedEnd <= clampedStart + 0.02) return null;

    return {
        start: clampedStart,
        end: clampedEnd,
        text: sub.text,
    };
}

/**
 * Format timestamp for SRT (HH:MM:SS,mmm)
 */
export const formatSRTTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const milliseconds = Math.round((seconds % 1) * 1000);

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')},${milliseconds.toString().padStart(3, '0')}`;
};

/**
 * Generate SRT content from raw subtitles list
 */
export const generateSRTFromSubtitles = (subtitles: Array<{ start: number; end: number; text: string }>): string => {
    if (!subtitles || subtitles.length === 0) return '';

    return subtitles.map((sub, index) => {
        const startTime = formatSRTTime(sub.start);
        const endTime = formatSRTTime(sub.end);
        const text = sub.text;

        return `${index + 1}\n${startTime} --> ${endTime}\n${text}\n`;
    }).join('\n');
};

/**
 * Generate SRT content from clips and active language
 */
export const generateSRTFromClips = (clips: Clip[], activeSubtitleLanguage: string): string => {
    if (!clips.length) return '';

    const totalDuration = Math.max(...clips.map(c => c.start + c.duration), 0);
    const collected: Array<{ start: number; end: number; text: string }> = [];
    const seen = new Set<string>();

    for (const clip of clips) {
        const langSubs = clip.subtitles?.[activeSubtitleLanguage] as NormalizableSubtitle[] | undefined;
        if (!langSubs || langSubs.length === 0) continue;

        const visibleSubs = langSubs.filter(sub => !sub.hidden);
        if (visibleSubs.length === 0) continue;

        const timingMode = detectSubtitleTimingMode(clip, visibleSubs);
        for (const sub of visibleSubs) {
            const normalized = normalizeSubtitleToTimeline(clip, sub, timingMode);
            if (!normalized) continue;

            const clampedStart = Math.max(0, normalized.start);
            const clampedEnd = Math.min(totalDuration, normalized.end);
            if (clampedEnd <= clampedStart + 0.02) continue;

            const key = `${clampedStart.toFixed(3)}|${clampedEnd.toFixed(3)}|${normalized.text}`;
            if (seen.has(key)) continue;
            seen.add(key);
            collected.push({
                start: clampedStart,
                end: clampedEnd,
                text: normalized.text,
            });
        }
    }

    collected.sort((a, b) => a.start - b.start);

    for (let i = 0; i < collected.length - 1; i++) {
        if (collected[i].end > collected[i + 1].start) {
            collected[i].end = collected[i + 1].start;
        }
    }

    const cleaned = collected.filter(sub => sub.end > sub.start + 0.05);
    return generateSRTFromSubtitles(cleaned);
};

/**
 * Universal SRT generator that handles both Clip[] and raw subtitle arrays.
 * Preserves backward compatibility for both App.tsx and ffmpeg-processor.ts usage.
 */
export function generateSRT(input: Clip[] | Array<{ start: number; end: number; text: string }>, activeSubtitleLanguage?: string): string {
    // Check if input is likely a raw subtitle array (has text/start/end but no id/url properties typical of clips)
    // Or check if properties exist on first element
    if (Array.isArray(input)) {
        if (input.length === 0) return '';

        const firstItem = input[0];
        // Check for Subtitle-like structure vs Clip-like structure
        const isSubtitle = 'text' in firstItem && 'start' in firstItem && 'end' in firstItem && !('subtitles' in firstItem);

        if (isSubtitle) {
            return generateSRTFromSubtitles(input as Array<{ start: number; end: number; text: string }>);
        }
    }

    if (!activeSubtitleLanguage) {
        throw new Error('activeSubtitleLanguage is required when generating SRT from Clips');
    }

    return generateSRTFromClips(input as Clip[], activeSubtitleLanguage);
}
